import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import UnsavedChangesModal from "../Components/UnsavedChangesModal";
import { Blocker } from "../hooks/useUnsavedChanges";

type UnsavedChangesCtxType = {
  isDirtyRef: React.MutableRefObject<boolean>;
  block: (proceed: () => void, reset: () => void) => void;
  safeNavigate: (to: string) => void;
};

const UnsavedChangesCtx = createContext<UnsavedChangesCtxType>({
  isDirtyRef: { current: false },
  block: () => {},
  safeNavigate: () => {},
});

export function useUnsavedChangesCtx() {
  return useContext(UnsavedChangesCtx);
}

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const isDirtyRef = useRef(false);
  const isBlockedRef = useRef(false);

  // I callback vengono tenuti in un ref separato dallo stato open/close.
  // Questo evita stale closures: il modal chiama sempre i callback più aggiornati.
  const callbacksRef = useRef<{ proceed: () => void; reset: () => void }>({
    proceed: () => {},
    reset: () => {},
  });

  const [isOpen, setIsOpen] = useState(false);

  const idle = useCallback(() => {
    isBlockedRef.current = false;
    setIsOpen(false);
  }, []);

  const block = useCallback((proceed: () => void, reset: () => void) => {
    callbacksRef.current = { proceed, reset };
    isBlockedRef.current = true;
    setIsOpen(true);
  }, []);

  // Il blocker passato alla modale usa wrapper stabili che leggono sempre
  // da callbacksRef — nessuna stale closure possibile.
  const blocker: Blocker = {
    state: isOpen ? "blocked" : "idle",
    proceed: () => { callbacksRef.current.proceed(); idle(); },
    reset: () => { callbacksRef.current.reset(); idle(); },
  };

  const safeNavigate = useCallback((to: string) => {
    if (!isDirtyRef.current) {
      navigate(to);
      return;
    }
    block(
      () => { isDirtyRef.current = false; navigate(to); },
      () => {}
    );
  }, [navigate, block]);

  // Listener globale per <a href> (navbar, sidebar, qualsiasi Link di React Router).
  // Sempre attivo al livello app — non dipende dal lifecycle del componente pagina.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirtyRef.current) return;
      if (isBlockedRef.current) return;

      const anchor = (e.target as Element).closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("http") ||
        href.startsWith("//") ||
        href.startsWith("#") ||
        href.startsWith("javascript")
      )
        return;

      e.preventDefault();
      e.stopPropagation();

      block(
        () => { isDirtyRef.current = false; navigate(href); },
        () => {}
      );
    };

    window.addEventListener("click", handleClick, true);
    return () => window.removeEventListener("click", handleClick, true);
  }, [navigate, block]);

  return (
    <UnsavedChangesCtx.Provider value={{ isDirtyRef, block, safeNavigate }}>
      {children}
      <UnsavedChangesModal blocker={blocker} />
    </UnsavedChangesCtx.Provider>
  );
}
